import { ArrowLeftOutlined, ExclamationCircleFilled, SyncOutlined } from "@ant-design/icons";
import { faRocket, faThLarge, faUsers } from "@fortawesome/free-solid-svg-icons";
import { Button, Tooltip } from "antd";
import React, { useState } from "react";
import { Link, useDataProvider, useNotify, usePermissions } from "react-admin";
import { ExtendedDataProvider } from '@src/types';
import CopyTooltip from "@src/ui/CopyTooltip";
import { getTagForChangeState, getTagForResponsesStatus, getTagForRuntimeStates } from "../../Common/Tags";
import FaIcon from "../../Icons/FaIcon";
import {
    CounterpartyName,
    CounterpartyPov,
    counterpartyOf,
} from "../../Teams/Counterparty";
import { formatDateAndTime, formatDateAndTimeFromNow } from "@src/helpers/utils";

/* A fact on the header's line, where there is room for the name but not for a
   label spelling out what kind of name it is — the icon carries that, and the
   tooltip spells it out along with the name in full, and copies it.
   Names here are whatever someone typed, so the value is cut to the space it
   has rather than wrapping and taking the line's alignment with it. `children`
   is for an adornment that must stay visible however long the name is;
   `valueNode` is the name itself when it is more than text, as the
   counterparty's is. */
const InlineFact = ({
    icon,
    label,
    value,
    valueNode,
    children,
}: {
    icon: any;
    label: string;
    value?: string;
    valueNode?: React.ReactNode;
    children?: React.ReactNode;
}) => (
    <CopyTooltip label={label} text={value}>
        <span className="sid-fact">
            <span className="sid-fact__ico" aria-hidden="true">{icon}</span>
            <span className="sid-sr">{label}: </span>
            <span className="sid-fact__value">{valueNode ?? value ?? '-'}</span>
            {children}
        </span>
    </CopyTooltip>
);

/* What this page calls the other party, on each side. The panel has a word of
   its own for it; this is the header's, and the service owner's was already
   here — the consumer's is its mirror. */
const COUNTERPARTY_LABEL: Record<CounterpartyPov, string> = {
    consumer: 'Consumer Team',
    serviceowner: 'Service Owner',
};

/**
 * The service item page's title card: the item's name, whom and what it
 * belongs to, and the state it is in — on one line, every name cut to fit,
 * with its tooltip holding it in full to read or copy. The healthcheck the
 * service last answered sits under the states, with the way to run it again.
 */
const ServiceItemTitleCard = ({
    record,
    pov,
    healthcheckResource,
}: {
    record: any;
    pov: string;
    healthcheckResource: string;
}) => {
    const [healthCheckTriggered, setHealthCheckTriggered] = useState(false);
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const { permissions } = usePermissions();
    const notify = useNotify();

    const triggerHealthcheckForServiceItem = (record: any) => {
        setHealthCheckTriggered(true);
        dataProvider.getAction(healthcheckResource, { verb: "trigger/service_item/" + record.id })
            .then((response: any) => {
                notify('Healthcheck triggered successfully')
                setHealthCheckTriggered(false)
            }).catch((error: any) => {
                // The API says why it refused — the healthcheck is paused, or
                // its rate limit is spent — and that reason is the only signal
                // this page has: the service carries `healthcheck` as a bare
                // boolean, so the button cannot know the check is switched off.
                notify(error.body?.trigger || 'Error triggering healthcheck', 'error')
                setHealthCheckTriggered(false)
            })
    }

    const permissionChangeState = ["ADMIN", "GENERAL"].includes(permissions);
    const backTo = pov === "serviceowner" ? "/owner_service_items" : "/service_items";

    /* The team on the other side, which is the side the reader is not on. This
       is the detail read rather than the light list one, so `service_owner_team`
       is there; `service.owner` is the same team by another route, kept as the
       fallback the lists fall back to. */
    const counterpartyPov = counterpartyOf(pov);
    const counterparty = counterpartyPov && {
        pov: counterpartyPov,
        team: counterpartyPov === 'consumer'
            ? record.consumer_team
            : record.service_owner_team ?? record.service?.owner,
    };

    return (
            <header className="sid-header">
            {/* Back, then the name, then everything else beside it — the arrow
                alone says where it goes back to, and the page has said the
                word "service item" enough by the time you are on one. */}
            <div className="sid-header__main">
                {/* The name and what it belongs to share the line with the
                    states, and give way by cutting: every name is cut to the
                    room it has rather than wrapping, its tooltip holding it in
                    full. */}
                <div className="sid-lead">
                    <div className="sid-identity">
                        <Tooltip title="Back to service items">
                            <Link className="sid-back" to={backTo} aria-label="Back to service items">
                                <ArrowLeftOutlined aria-hidden="true" />
                            </Link>
                        </Tooltip>
                        {/* cut to the line rather than wrapping onto a second
                            one; the tooltip has the name in full */}
                        <CopyTooltip placement="bottomLeft" text={record.name}>
                            <h1 className="sid-title">{record.name || "Untitled service item"}</h1>
                        </CopyTooltip>
                    </div>

                    <div className="sid-facts">
                        {/* Whoever is on the other side of this item, on
                            both sides: the owner's consumer was already
                            named here, and now that the API will hand over
                            a counterparty's team, the consumer's owner is
                            worth naming too. Either opens the team. */}
                        {counterparty && (
                            <InlineFact
                                icon={FaIcon(faUsers)}
                                label={COUNTERPARTY_LABEL[counterparty.pov]}
                                value={counterparty.team?.name}
                                valueNode={
                                    <CounterpartyName
                                        team={counterparty.team}
                                        pov={counterparty.pov}
                                        label={COUNTERPARTY_LABEL[counterparty.pov]}
                                        fallback="-"
                                    />
                                }
                            />
                        )}
                        <InlineFact
                            icon={FaIcon(faThLarge)}
                            label="Application"
                            value={record.application?.name}
                        />
                        <InlineFact
                            icon={FaIcon(faRocket)}
                            label="Service"
                            value={record.service?.name}
                        >
                            {record.service?.name && record.is_deprecated_service_schema && (
                                <Tooltip title="The service has a new schema" color="orange" placement="topRight">
                                    <ExclamationCircleFilled
                                        className="sid-fact__warn"
                                        aria-label="The service has a new schema"
                                    />
                                </Tooltip>
                            )}
                        </InlineFact>
                    </div>
                </div>

                {/* What the item is right now, held at the end of the card:
                    its states on the first line and, under them, the health
                    the service last reported. */}
                <div className="sid-header__aside">
                    <div className="sid-header__states">
                        {getTagForRuntimeStates(record.runtime_state)}
                        {getTagForChangeState(record.change_state)}
                        {record.created && (
                            <Tooltip title={formatDateAndTime(record.created)}>
                                <span className="sid-created">
                                    Created {formatDateAndTimeFromNow(record.created)}
                                </span>
                            </Tooltip>
                        )}
                    </div>

                    {record?.service?.healthcheck && (
                        <div className="sid-health">
                            <span className="sid-health__label">Healthcheck</span>
                            <Tooltip title="Last Healthcheck Response">
                                {getTagForResponsesStatus(record.healthcheck_status)}
                            </Tooltip>
                            {permissionChangeState && (
                                <Tooltip title="Trigger Healthcheck">
                                    <Button
                                        size="small"
                                        aria-label="Trigger healthcheck"
                                        loading={healthCheckTriggered}
                                        onClick={() => triggerHealthcheckForServiceItem(record)}
                                        icon={<SyncOutlined />}
                                    >
                                        Run
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default ServiceItemTitleCard;
